"use client";

import { FunctionComponent, PropsWithChildren, ReactElement, createContext, useMemo } from "react";
import toast, { ToastOptions, ToastType, Toaster, Toast as HotToast } from "react-hot-toast";
import { Toast } from "../toast/toast";
import { TailwindColors } from "../types/general";
import { Spinner } from "../spinner/spinner";

export interface ToastProviderProps {}

interface CustomToastOptions extends ToastOptions {
    /**
     * Color of the toast.
     */
    color?: keyof TailwindColors | undefined;

    /**
     * Colorize only icon.
     */
    colorizeOnlyIcon?: boolean;

    /**
     * Border radius of the toast.
     */
    rounded?: "md" | "lg";
}

type CustomToast = {
    [K in ToastType]: (message: string, options?: CustomToastOptions) => string;
} & ((message: string, options?: CustomToastOptions) => string) &
    typeof toast;

export interface ToastContextType {
    toast: CustomToast;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: FunctionComponent<PropsWithChildren<ToastProviderProps>> = ({ children }) => {
    const value = useMemo(() => ({ toast }), []) as ToastContextType;

    return (
        <ToastContext.Provider value={value}>
            {children}
            <Toaster gutter={12}>
                {(t: HotToast & CustomToastOptions) => {
                    const positionArr = t.position?.split("-") || ["top"];
                    const color = t.color || (t.type === "success" ? "green" : t.type === "error" ? "red" : "gray");
                    const type = t.type === "success" ? "success" : t.type === "error" ? "error" : "info";

                    return (
                        <Toast
                            color={color}
                            type={t.type === "loading" ? undefined : type}
                            icon={t.type === "loading" ? <Spinner color="white" /> : undefined}
                            rounded={t.rounded || "lg"}
                            colorizeOnlyIcon={t.colorizeOnlyIcon === false ? false : true}
                            message={t.message as ReactElement | string}
                            position={positionArr[0] === "top" ? "top" : "bottom"}
                            onClose={() => toast.dismiss(t.id)}
                            visible={t.visible}
                            style={t.style}
                        />
                    );
                }}
            </Toaster>
        </ToastContext.Provider>
    );
};
